import React, { useCallback, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import api from "../../api";
import usePageMeta from "../../hooks/usePageMeta";
import { getAllStoresAction } from "../../redux/actions/adminActions";
import AnalyticsDashboard from "../analytics/AnalyticsDashboard";
import {
    buildParams,
    CHART_COLORS,
    fmtCurrency,
    fmtNumber,
    MONTH_OPTIONS,
} from "../analytics/analyticsUtils";

const now = new Date();
const YEARS = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

const TotalCard = ({ label, value, icon, color, sub }) => (
    <div className="bg-white rounded-2xl shadow-1 p-4 flex flex-col gap-2">
        <div className="flex items-center justify-between">
            <span className="text-[12px] uppercase tracking-wide text-slate-500 font-semibold">{label}</span>
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-white ${color}`}>
                <i className={`fa-solid ${icon} text-sm`} />
            </span>
        </div>
        <span className="text-[24px] leading-none font-bold text-slate-800">{value}</span>
        {sub && <span className="text-[12px] text-slate-400">{sub}</span>}
    </div>
);

const AdminAnalytics = () => {
    const dispatch = useDispatch();

    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [drillStore, setDrillStore] = useState("");

    usePageMeta("adminAnalytics");

    useEffect(() => {
        dispatch(getAllStoresAction());
    }, [dispatch]);

    const fetchOverview = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildParams({ year, month, months: 12 });
            const { data: res } = await api.get(`/api/v1/analytics/admin/overview?${params.toString()}`);
            if (res.success) setData(res);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to load cross-store analytics");
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => {
        const timer = setTimeout(fetchOverview, 250);
        return () => clearTimeout(timer);
    }, [fetchOverview]);

    const totals = data?.totals || {};
    const leaderboard = data?.leaderboard || [];
    const risks = data?.cross_store_risks || [];

    const chart = useMemo(() => {
        const top = (data?.leaderboard || []).slice(0, 10);
        return {
            options: {
                chart: { toolbar: { show: false }, fontFamily: "inherit" },
                colors: [CHART_COLORS.blue],
                plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "60%" } },
                dataLabels: {
                    enabled: true,
                    formatter: (val) => fmtCurrency(val),
                    style: { fontSize: "11px", colors: ["#fff"] },
                },
                grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
                xaxis: {
                    categories: top.map((s) => s.store_name),
                    labels: { style: { colors: "#94a3b8", fontSize: "11px" } },
                },
                yaxis: { labels: { style: { colors: "#475569", fontSize: "11px" } } },
                tooltip: { y: { formatter: (val) => fmtCurrency(val) } },
            },
            series: [{ name: "Revenue", data: top.map((s) => s.sales_revenue) }],
            hasData: top.some((s) => s.sales_revenue > 0),
        };
    }, [data]);

    return (
        <div className="pl-[1.5vmax] pr-[2.5vmax] mt-[1vmax] mb-[4vmax] flex flex-col gap-4">
            <div className="flex flex-wrap justify-between items-center gap-3 bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax]">
                <h2 className="text-[1.3vmax] font-bold">Analytics &amp; Forecasting</h2>
                <div className="flex items-center gap-2">
                    <select
                        value={month}
                        onChange={(e) => setMonth(Number(e.target.value))}
                        className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] outline-[var(--color-2)]"
                    >
                        {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <select
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] outline-[var(--color-2)]"
                    >
                        {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>

            {/* Cross-store totals */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                <TotalCard label="Active Stores" value={fmtNumber(totals.stores)} icon="fa-store" color="bg-[var(--color-2)]" />
                <TotalCard label="Inventory (Cost)" value={fmtCurrency(totals.inventory_cost)} icon="fa-warehouse" color="bg-slate-600" />
                <TotalCard label="Inventory (Retail)" value={fmtCurrency(totals.inventory_retail)} icon="fa-tags" color="bg-purple-500" />
                <TotalCard label="Sales Units" value={fmtNumber(totals.sales_units)} icon="fa-boxes-stacked" color="bg-blue-500" sub={data?.period?.label} />
                <TotalCard label="Sales Revenue" value={fmtCurrency(totals.sales_revenue)} icon="fa-cash-register" color="bg-green-500" />
                <TotalCard label="Reorder Alerts" value={fmtNumber(totals.reorder_count)} icon="fa-triangle-exclamation" color="bg-red-500" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Revenue leaderboard chart */}
                <div className="bg-white rounded-2xl shadow-1 p-4">
                    <h3 className="font-bold text-slate-800 text-[15px] mb-2">Store Revenue Leaderboard</h3>
                    {loading ? (
                        <div className="h-[340px] flex-center text-slate-400 text-[13px]">Loading…</div>
                    ) : chart.hasData ? (
                        <Chart options={chart.options} series={chart.series} type="bar" height={340} />
                    ) : (
                        <div className="h-[340px] flex-center text-slate-400 text-[13px]">No sales this period.</div>
                    )}
                </div>

                {/* Cross-store stockout risks */}
                <div className="bg-white rounded-2xl shadow-1 p-4">
                    <h3 className="font-bold text-slate-800 text-[15px] mb-2">
                        <i className="fa-solid fa-triangle-exclamation text-red-500 mr-2" />
                        Top Stockout Risks (All Stores)
                    </h3>
                    <div className="overflow-y-auto my-scroll max-h-[340px]">
                        {risks.length === 0 ? (
                            <div className="h-[300px] flex-center text-slate-400 text-[13px]">
                                No stockout risks detected.
                            </div>
                        ) : (
                            <table className="w-full text-[13px]">
                                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                                    <tr>
                                        <th className="th-text px-3 py-2 font-semibold">Store</th>
                                        <th className="th-text px-3 py-2 font-semibold">Product</th>
                                        <th className="th-num px-3 py-2 font-semibold">Cover</th>
                                        <th className="th-num px-3 py-2 font-semibold">Order</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {risks.map((r, i) => (
                                        <tr key={`${r.store_id}-${r.upc}-${i}`} className="border-b border-slate-50">
                                            <td className="td-text px-3 py-2 text-slate-600">{r.store_name}</td>
                                            <td className="td-text px-3 py-2">
                                                <p className="font-semibold text-slate-700 truncate max-w-[160px]">{r.description}</p>
                                                <p className="text-[11px] text-slate-400">{r.upc}</p>
                                            </td>
                                            <td className="td-num px-3 py-2 font-bold text-red-600">
                                                {r.days_of_cover === null ? "—" : `${r.days_of_cover}d`}
                                            </td>
                                            <td className="td-num px-3 py-2 font-bold text-[var(--color-2)]">
                                                {fmtNumber(r.suggested_order_qty)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Leaderboard table */}
            <div className="bg-white rounded-2xl shadow-1 p-4">
                <h3 className="font-bold text-slate-800 text-[15px] mb-3">Store Performance</h3>
                <div className="overflow-x-auto my-scroll">
                    <table className="w-full text-[13px]">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="th-text px-3 py-2 font-semibold">Store</th>
                                <th className="th-num px-3 py-2 font-semibold">Sales Units</th>
                                <th className="th-num px-3 py-2 font-semibold">Sales Revenue</th>
                                <th className="th-num px-3 py-2 font-semibold">Inventory (Cost)</th>
                                <th className="th-num px-3 py-2 font-semibold">SKUs</th>
                                <th className="th-num px-3 py-2 font-semibold">Reorder</th>
                                <th className="th-center px-3 py-2 font-semibold">Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaderboard.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center text-slate-400 py-8">
                                        {loading ? "Loading…" : "No stores found."}
                                    </td>
                                </tr>
                            ) : (
                                leaderboard.map((s) => (
                                    <tr key={s.store_id} className="border-b border-slate-50 hover:bg-slate-50">
                                        <td className="td-text px-3 py-2 font-semibold text-slate-700">{s.store_name}</td>
                                        <td className="td-num px-3 py-2 text-slate-600">{fmtNumber(s.sales_units)}</td>
                                        <td className="td-num px-3 py-2 font-bold text-green-600">{fmtCurrency(s.sales_revenue)}</td>
                                        <td className="td-num px-3 py-2 text-slate-600">{fmtCurrency(s.inventory_cost)}</td>
                                        <td className="td-num px-3 py-2 text-slate-500">{fmtNumber(s.sku_count)}</td>
                                        <td className="td-num px-3 py-2">
                                            {s.reorder_count > 0 ? (
                                                <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[12px] font-bold">
                                                    {s.reorder_count}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400">0</span>
                                            )}
                                        </td>
                                        <td className="td-center px-3 py-2">
                                            <button
                                                onClick={() =>
                                                    setDrillStore(
                                                        drillStore === String(s.store_id) ? "" : String(s.store_id)
                                                    )
                                                }
                                                className="text-[var(--color-2)] hover:opacity-70 font-semibold text-[12px]"
                                            >
                                                {drillStore === String(s.store_id) ? "Hide" : "View"}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Drill-down: full per-store dashboard */}
            {drillStore && (
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-slate-800 text-[15px]">
                            <i className="fa-solid fa-magnifying-glass-chart text-[var(--color-2)] mr-2" />
                            Store Detail —{" "}
                            {leaderboard.find((s) => String(s.store_id) === drillStore)?.store_name || ""}
                        </h3>
                        <button
                            onClick={() => setDrillStore("")}
                            className="text-slate-400 hover:text-slate-600"
                        >
                            <i className="fa-solid fa-xmark" />
                        </button>
                    </div>
                    <AnalyticsDashboard embedded storeIdOverride={drillStore} />
                </div>
            )}
        </div>
    );
};

export default AdminAnalytics;
